import {
  BookingResourceFragment,
  BookingSlotFragment,
} from '@/graphql/custom-types';
import {
  BookingInput,
  BookingResourceBookingSlot,
  BookResourceScreenDocument,
  BookSlottedResourceDocument,
  CancelSlottedBookingDocument,
  GetBookingResourcesDocument,
  GetBookingSlotsDocument,
  ProfileInput,
  ScreenProfile,
} from '@/graphql/generated-types';
import { apolloClient } from '@/utils/apollo';
import SentryGraphQLError from '@/utils/exceptions/SentryGraphQLError';

const bookResourceScreen = async (
  participants: ProfileInput[],
  booking: BookingInput
) => {
  const response = await apolloClient.mutate({
    mutation: BookResourceScreenDocument,
    variables: {
      participants,
      booking,
    },
    errorPolicy: 'all',
  });

  if (response.data?.booking?.saveBooking?.success) {
    return response.data.booking.saveBooking;
  }

  throw new SentryGraphQLError(`api.booking.bookResourceScreen`, response);
};

const bookSlottedResource = async (
  participants: ProfileInput[],
  bookingSlot: BookingResourceBookingSlot
) => {
  const response = await apolloClient.mutate({
    mutation: BookSlottedResourceDocument,
    variables: {
      participants,
      bookingSlot,
    },
    errorPolicy: 'all',
  });

  if (response.data?.booking?.saveSlottedBooking?.success) {
    return response.data.booking.saveSlottedBooking;
  }

  throw new SentryGraphQLError(`api.booking.bookSlottedResource`, response);
};

const cancelSlottedBooking = async (
  profile: ScreenProfile,
  bookingSlot: BookingResourceBookingSlot
) => {
  const response = await apolloClient.mutate({
    mutation: CancelSlottedBookingDocument,
    variables: {
      profile,
      bookingSlot,
    },
    errorPolicy: 'all',
  });

  if (response.data?.booking?.cancelSlottedBooking?.success) {
    return response.data.booking.cancelSlottedBooking;
  }

  throw new SentryGraphQLError(`api.booking.cancelSlottedBooking`, response);
};

const getBookingResources = async (
  departmentId: string
): Promise<BookingResourceFragment[]> => {
  const response = await apolloClient.query({
    query: GetBookingResourcesDocument,
    variables: {
      filter: {
        departments: [departmentId],
      },
      timezone: 'Europe/Copenhagen',
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  if (response.data?.bookingResources) {
    return response.data.bookingResources;
  }

  throw new SentryGraphQLError(`api.booking.getBookingResources`, response);
};

const getBookingSlots = async (
  bookingResourceId: string,
  dateFrom: string,
  dateTo: string
): Promise<BookingSlotFragment[]> => {
  const response = await apolloClient.query({
    query: GetBookingSlotsDocument,
    variables: {
      bookingResourceId,
      dateFrom,
      dateTo,
      timezone: 'Europe/Copenhagen',
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  if (response.data?.bookingSlots) {
    return response.data.bookingSlots;
  }

  throw new SentryGraphQLError(`api.booking.getBookingSlots`, response);
};

export default {
  bookResourceScreen,
  bookSlottedResource,
  cancelSlottedBooking,
  getBookingResources,
  getBookingSlots,
};
